import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import PrimaryNavigation from '../../components/ui/PrimaryNavigation';
import ContextualBreadcrumb from '../../components/ui/ContextualBreadcrumb';
import FloatingActionButton from '../../components/ui/FloatingActionButton';
import WorkerProjectCard from './components/WorkerProjectCard';
import EmptyWorkerState from './components/EmptyWorkerState';
import { projectService } from '../../services/projectService';
import { realtimeService } from '../../services/realtimeService';

const WorkerHomePage = () => {
  const navigate = useNavigate();
  const { user, userProfile, loading: authLoading } = useAuth();

  const [projects, setProjects] = useState([]);
  const [activeTab, setActiveTab] = useState('active');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!authLoading && user) {
      loadProjects();
    } else if (!authLoading && !user) {
      setError('Please log in to view your projects');
      setLoading(false);
    }
  }, [authLoading, user]);

  useEffect(() => {
    return () => {
      realtimeService?.unsubscribeAll();
    };
  }, []);

  const normalizePhaseStatus = (status) => {
    return String(status || 'pending')?.toLowerCase();
  };

  const isPhaseCompleted = (phase) => {
    return normalizePhaseStatus(phase?.status) === 'completed';
  };

  const isPhaseActive = (phase) => {
    const status = normalizePhaseStatus(phase?.status);

    return (
      status === 'ongoing' ||
      status === 'in-progress' ||
      status === 'active'
    );
  };

  const handlePhaseUpdate = (updatedPhase) => {
    setProjects(prev =>
      prev?.map(project => {
        const phaseIndex = project?.workerPhases?.findIndex(
          phase => phase?.phaseId === updatedPhase?.id
        );

        if (phaseIndex === -1) return project;

        const updatedPhases = [...project?.workerPhases];

        updatedPhases[phaseIndex] = {
          ...updatedPhases?.[phaseIndex],
          progress: updatedPhase?.progress,
          status: updatedPhase?.status
        };

        return {
          ...project,
          workerPhases: updatedPhases
        };
      })
    );
  };

  const loadProjects = async () => {
    try {
      setLoading(true);
      setError('');

      const data = await projectService?.getWorkerProjects(user?.id);

      setProjects(data || []);

      const ids = (data || [])?.map(project => project?.id);

      if (ids?.length > 0) {
        realtimeService?.subscribeToMultipleProjects(ids, handlePhaseUpdate);
      }
    } catch (err) {
      console.error('Error loading worker projects:', err);
      setError(err?.message || 'Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const handleNavigateToPhase = (projectId, projectName, phase, projectSource) => {
    navigate(`/workers-manage-phase?phaseId=${phase?.phaseId}&projectId=${projectId}`, {
      state: {
        projectId,
        projectName,
        phaseId: phase?.phaseId,
        projectSource,
        phase: {
          id: phase?.phaseId,
          name: phase?.phaseName,
          status: phase?.status,
          progress: phase?.progress || 0,
          suggestedStart: phase?.estimatedStartDate,
          suggestedEnd: phase?.estimatedEndDate
        },
        workerRole: userProfile?.trade
      }
    });
  };

  const getWorkerEffectiveStatus = (project) => {
    const phases = project?.workerPhases || [];

    if (phases?.length > 0 && phases?.every(isPhaseCompleted)) {
      return 'completed';
    }

    if (phases?.some(isPhaseActive)) {
      return 'active';
    }

    return 'dormant';
  };

  const dormantProjects = projects?.filter(
    project => getWorkerEffectiveStatus(project) === 'dormant'
  );

  const activeProjects = projects?.filter(
    project => getWorkerEffectiveStatus(project) === 'active'
  );

  const completedProjects = projects?.filter(
    project => getWorkerEffectiveStatus(project) === 'completed'
  );

  const getTabProjects = () => {
    let list;

    if (activeTab === 'pending') {
      list = dormantProjects;
    } else if (activeTab === 'completed') {
      list = completedProjects;
    } else {
      list = activeProjects;
    }

    if (!searchQuery?.trim()) return list;

    return list?.filter(project =>
      project?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase())
    );
  };

  const displayProjects = getTabProjects();
  const hasProjects = projects?.length > 0;

  const tabs = [
    {
      key: 'active',
      label: 'Active',
      count: activeProjects?.length
    },
    {
      key: 'pending',
      label: 'Pending',
      count: dormantProjects?.length
    },
    {
      key: 'completed',
      label: 'Completed',
      count: completedProjects?.length
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <PrimaryNavigation />
        <ContextualBreadcrumb />

        <div className="main-content-mobile-nav flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <PrimaryNavigation />
        <ContextualBreadcrumb />

        <div className="main-content-mobile-nav flex items-center justify-center py-12">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error}</p>

            <button
              onClick={loadProjects}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <PrimaryNavigation />
      <ContextualBreadcrumb />

      <div className="main-content-mobile-nav max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">My Projects</h1>
          <p className="text-gray-600">View your assigned projects and manage your phases</p>
        </div>

        {hasProjects ? (
          <>
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1 mb-6 w-fit">
              {tabs?.map(tab => (
                <button
                  key={tab?.key}
                  onClick={() => setActiveTab(tab?.key)}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                    activeTab === tab?.key
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {tab?.label}

                  {tab?.count > 0 && (
                    <span
                      className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                        activeTab === tab?.key
                          ? 'bg-primary text-white'
                          : 'bg-gray-200 text-gray-600'
                      }`}
                    >
                      {tab?.count}
                    </span>
                  )}
                </button>
              ))}
            </div>

            <div className="mb-6">
              <input
                type="text"
                placeholder="Search projects..."
                value={searchQuery}
                onChange={event => setSearchQuery(event?.target?.value)}
                className="w-full max-w-sm px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
              />
            </div>

            {displayProjects?.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500">
                  {searchQuery ? 'No projects match your search' : `No ${activeTab} projects`}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {displayProjects?.map(project => (
                  <WorkerProjectCard
                    key={project?.id}
                    project={project}
                    lifecycleStatus={getWorkerEffectiveStatus(project)}
                    currentUserRole={userProfile?.trade}
                    onNavigateToPhase={(projectId, projectName, phase) =>
                      handleNavigateToPhase(projectId, projectName, phase, project?.source)
                    }
                  />
                ))}
              </div>
            )}
          </>
        ) : (
          <EmptyWorkerState />
        )}
      </div>

      <FloatingActionButton />
    </div>
  );
};

export default WorkerHomePage;
